import { ErreurApi, type Client } from '../../api/client.js';
import { ECHANGES_INDISPONIBLES, PARTAGE_INDISPONIBLE_DROIT, PARTAGE_INDISPONIBLE_PANNE } from './libelles.js';
import type {
  CanalConsentement,
  ConsentementLu,
  CorpsPartage,
  DestinatairePartage,
  DonneesFiche,
  EtiquetteSensible,
  FilEchange,
  FinaliteConsentement,
  PersonneFiche,
  PieceAttendue,
  PlanRelance,
} from './modele.js';

export interface SourceFiche {
  charger: (personId: string, dossierId: string) => Promise<DonneesFiche>;
  enregistrerConsentement: (personId: string, finalite: FinaliteConsentement, accorde: boolean, canal: CanalConsentement) => Promise<void>;
  partager: (etiquetteId: string, corps: CorpsPartage) => Promise<void>;
  pauser: (planId: string, motif: string) => Promise<void>;
  reprendre: (planId: string) => Promise<void>;
}

/**
 * Externes rattachés au dossier que l'on peut PROPOSER à l'alerte de partage (jamais envoyés d'office : l'équipe coche chaque
 * nom), lus sur la route dédiée (RFC-L55-001 point 1, ARB-VERROU-05) sous le droit de partager. Sans ce droit, ou en cas de
 * panne, aucun destinataire n'est proposé et la raison est donnée à l'écran : échec sûr, jamais une liste vide muette.
 */
async function destinatairesProposes(
  client: Client,
  dossierId: string,
): Promise<{ liste: DestinatairePartage[]; indisponible: string | null }> {
  try {
    const liste = await client.appeler<DestinatairePartage[]>('confidentialite.destinataires', { parametres: { dossierId } });
    return { liste, indisponible: null };
  } catch (erreur) {
    const droit = erreur instanceof ErreurApi && (erreur.status === 403 || erreur.code === 'FORBIDDEN');
    return { liste: [], indisponible: droit ? PARTAGE_INDISPONIBLE_DROIT : PARTAGE_INDISPONIBLE_PANNE };
  }
}

/** Accès aux routes du contrat pour la fiche héritier. Les écritures passent toutes par des routes auditées. */
export function creerSource(client: Client): SourceFiche {
  return {
    async charger(personId, dossierId) {
      const [personne, consentements, sensibles, pieces, plans, fils, proposes] = await Promise.all([
        client.appeler<PersonneFiche>('genealogie.personne.lire', { parametres: { personId } }),
        client.appeler<ConsentementLu[]>('confidentialite.consentements', { parametres: { personId } }),
        client.appeler<EtiquetteSensible[]>('confidentialite.sensibles', { parametres: { personId } }),
        client.appeler<PieceAttendue[]>('documents.attendus', { parametres: { dossierId } }),
        client.appeler<PlanRelance[]>('relances.plans', { requete: { dossierId } }),
        // Messagerie indisponible (501 tant que son adaptateur manque) ou en panne : la fiche se charge sans les échanges
        // et dit pourquoi (PER-2-12, PER-7-05 : un seul échec faisait tomber toute la fiche).
        client.appeler<{ items: FilEchange[] }>('messagerie.fils', { requete: { dossierId } }).catch(() => null),
        destinatairesProposes(client, dossierId),
      ]);
      return {
        personne,
        consentements,
        sensibles,
        pieces: pieces.filter((p) => p.personId === personId),
        plans: plans.filter((p) => p.personId === personId),
        echanges: fils === null ? [] : fils.items.filter((f) => f.personId === personId),
        echangesIndisponible: fils === null ? ECHANGES_INDISPONIBLES : null,
        destinataires: proposes.liste,
        partageIndisponible: proposes.indisponible,
      };
    },
    async enregistrerConsentement(personId, finalite, accorde, canal) {
      await client.appeler('confidentialite.consentements.enregistrer', {
        parametres: { personId },
        corps: { purpose: finalite, granted: accorde, channel: canal },
      });
    },
    async partager(etiquetteId, corps) {
      await client.appeler('confidentialite.sensibles.partager', { parametres: { etiquetteId }, corps });
    },
    async pauser(planId, motif) {
      await client.appeler('relances.pause', { parametres: { planId }, corps: { reason: motif, until: null } });
    },
    async reprendre(planId) {
      await client.appeler('relances.reprise', { parametres: { planId } });
    },
  };
}
